import { styled } from "goober";
import { useEffect, useState } from "react";
import { Box } from "../Box";
import { ClickButton } from "../ClickButton";
import { Icon } from "../Icon";
import { Text } from "../Text";
import { CopyableValue } from "./CopyableValue";
import { FieldLabel } from "./shared";

const TYPEWRITER_MS_PER_CHAR = 12;

const KeyField = styled(ClickButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 0.5px solid var(--border-alpha);
  background: var(--alpha2);
  color: var(--text-secondary);
  cursor: pointer;
  &:hover:not(:disabled) {
    background: var(--alpha4);
  }
  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

type Props = {
  label: string;
  generateLabel: string;
  revealedKey: string | null;
  generating: boolean;
  disabled?: boolean;
  onGenerate: () => void;
};

export function GeneratedKeyField({
  label,
  generateLabel,
  revealedKey,
  generating,
  disabled,
  onGenerate,
}: Props) {
  const [typedCount, setTypedCount] = useState(0);

  // Typewriter the freshly generated key in, char by char.
  useEffect(() => {
    if (revealedKey === null) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
      // oxlint-disable-next-line react/react-compiler -- EffectSetState: jumps to the full length under reduced motion instead of running the typewriter interval
      setTypedCount(revealedKey.length);
      return;
    }
    setTypedCount(0);
    let count = 0;
    const id = setInterval(() => {
      count += 1;
      setTypedCount(count);
      if (count >= revealedKey.length) clearInterval(id);
    }, TYPEWRITER_MS_PER_CHAR);
    return () => clearInterval(id);
  }, [revealedKey]);

  return (
    <FieldLabel>
      <Box horizontal={8} vertical={10}>
        <Text variant="text-small-md">{label}</Text>
      </Box>
      {revealedKey !== null ? (
        <CopyableValue value={revealedKey}>
          <Text
            ellipsis
            variant="text-regular"
            color="var(--text-secondary)"
            style={{ flex: 1 }}
          >
            {revealedKey.slice(0, typedCount)}
          </Text>
        </CopyableValue>
      ) : (
        <KeyField
          data-no-pill
          disabled={disabled || generating}
          onClick={onGenerate}
        >
          <Icon name="key" size={12} />
          <Text variant="text-regular" color="var(--text-secondary)">
            {generating ? "Generating…" : generateLabel}
          </Text>
        </KeyField>
      )}
      <Box horizontal={8} top={6}>
        <Text variant="text-small" color="var(--text-secondary)">
          {revealedKey !== null
            ? "Copy it now — you won’t see it again."
            : "Shown only once, right after you generate it."}
        </Text>
      </Box>
    </FieldLabel>
  );
}
